import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JobStatus } from '../domain/enums/jobStatus';
import { TranslateService } from 'ng2-translate/ng2-translate';
import { IMyDateModel, IMyOptions, IMyDate } from 'ngx-mydatepicker';
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';

import { JobType } from '../domain/enums/jobType';
import { PositionType } from '../domain/enums/positionType';
import { CalculateQuotaRequest, CalculateQuotaResponse } from '../domain/models/calculateQuota';
import { Job } from '../domain/models/job';
import { Position } from '../domain/models/position';
import { Project } from '../domain/models/project';
import { Size } from '../domain/models/size';
import { UserData } from '../domain/models/userData';
import { DialogModel } from '../shared/components/dialog/dialog.model';
import { LimitToPipe } from '../shared/pipes/limit-to.pipe';
import { NewRouteViewModel, WayPointViewModel } from './new-route.viewModel';
import { EditRouteViewModel } from './edit-route.viewModel';
import { NewRouteService } from './services/new-route.service';

import { tryParseInt } from '../../utils/try-parse';
import * as _ from 'lodash';
import * as moment from 'moment/moment';

@Component({
  selector: 'app-new-route',
  templateUrl: './new-route.component.html',
  styleUrls: ['./new-route.component.scss']
})
export class NewRouteComponent implements OnInit, OnDestroy {
  public project: Project;
  public projectId: string;

  public routeName: string;
  public startDate: Date;
  public endDate: Date;
  public time: any = { startHour: 0, startMinutes: 0, endHour: 23, endMinutes: 59 };

  private draftJobId: string;
  private startTimeString: string;
  private endTimeString: string;

  public startPoint: WayPointViewModel;
  public endPoint: WayPointViewModel;
  public waypoints: WayPointViewModel[];
  public locationsCache: WayPointViewModel[];

  public roadSegment: number;
  public interval: number;
  public days: any = {
    monday: true, tuesday: true, wednesday: true,
    thursday: true, friday: true, saturday: true, sunday: true
  };
  public requestsPerDay: number;
  public blueLineRequests: number;
  public totalRequests: number;
  public datePickerConfig: any;
  public spectrogram: string;
  public calculateSpectrogram: boolean;
  public calculateBudgetSpectrogram: boolean;
  public calculatehistorical: boolean;
  public branchingAnalyse: boolean;
  public routeLength: number;
  public expectedRouteLength: number;
  public quotaShortage: number;
  public datePickerMin: IMyDate;
  public timeInput: any;
  public newRouteDialog: DialogModel;
  public startDatePickerOptions: IMyOptions;
  public endDatePickerOptions: IMyOptions;

  private createdShown: boolean;
  private createdFailed: boolean;
  private createInProgress: boolean;
  private insufficientQuota: boolean;

  private selectedDays: number[] = [0, 1, 2, 3, 4, 5, 6];

  private newRouteDialogVisible: boolean;
  private hideTutorial: boolean;

  private fromDatePicker: any;
  private toDatePicker: any;
  private showErrors: boolean;
  private timeInputObservable: Subject<any>;
  private subscriptions: Subscription[];
  private selectedDaysValid: boolean;
  private editMode: boolean;

  public NO_SPEC = 'noSpectrogram';
  public NORMAL_SPEC = 'calculateSpectrogram';
  public BUDGET_SPEC = 'calculateBudgetSpectrogram';

  constructor(
    private ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private newRouteService: NewRouteService,
    private router: Router,
    private translateService: TranslateService) {
  }

  ngOnInit() {
    this.setDatePickerOptions();
    this.startTimeString = '00:00:00';
    this.endTimeString = '23:59:59';
    this.createdShown = false;
    this.createdFailed = false;
    this.startDate = null;
    this.endDate = null;
    this.spectrogram = 'noSpectrogram';
    this.calculateSpectrogram = false;
    this.calculateBudgetSpectrogram = false;
    this.calculatehistorical = true;
    this.branchingAnalyse = false;
    this.hideTutorial = true;
    this.subscriptions = [];
    if (this.fromDatePicker && this.fromDatePicker.jsdate) {
      this.startDate = this.fromDatePicker.jsdate;
    }
    if (this.toDatePicker && this.toDatePicker.jsdate) {
      this.endDate = this.toDatePicker.jsdate;
    }

    this.interval = 15;
    this.roadSegment = 150;
    this.showErrors = false;
    this.waypoints = [];
    this.locationsCache = [];

    this.timeInputObservable = new Subject<any>();
    this.newRouteService.clearRoute();

    this.getJobOrProjectSettings();

    this.getUserSettings();
    this.initMapClick();
    this.initOnRouteChanged();
    this.newRouteService.enableSearch();


    this.newRouteDialog = new DialogModel(
      'CREATE_NEW_ROUTE',
      ['ENTER_DESTINATION_SEARCH_DESCRIPTION',
        'ENTER_START_AND_ENDPOINT',
        'CHANGE_ROUTE_DESCRIPTION',
        'ADD_WAYPOINTS_DESCRIPTION',
        'CREATE_BUTTON_DESCRIPTION'],
      'CONTINUE');

    this.newRouteDialogVisible = true;

    this.translateService.onLangChange.subscribe(() => {
      this.setDatePickerOptions();
    });
  }

  private setDatePickerOptions(): void {
    let today = new Date();
    this.datePickerMin = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() - 1 };
    let defaultOptions = {
      disableUntil: this.datePickerMin,
      dateFormat: 'dd/mm/yyyy',
      dayLabels: {
        su: new LimitToPipe().transform(this.translateService.instant('SUNDAY'), 3),
        mo: new LimitToPipe().transform(this.translateService.instant('MONDAY'), 3),
        tu: new LimitToPipe().transform(this.translateService.instant('TUESDAY'), 3),
        we: new LimitToPipe().transform(this.translateService.instant('WEDNESDAY'), 3),
        th: new LimitToPipe().transform(this.translateService.instant('THURSDAY'), 3),
        fr: new LimitToPipe().transform(this.translateService.instant('FRIDAY'), 3),
        sa: new LimitToPipe().transform(this.translateService.instant('SATURDAY'), 3)
      },
      monthLabels: {
        1: new LimitToPipe().transform(this.translateService.instant('JANUARY'), 3),
        2: new LimitToPipe().transform(this.translateService.instant('FEBRUARY'), 3),
        3: new LimitToPipe().transform(this.translateService.instant('MARCH'), 3),
        4: new LimitToPipe().transform(this.translateService.instant('APRIL'), 3),
        5: new LimitToPipe().transform(this.translateService.instant('MAY'), 3),
        6: new LimitToPipe().transform(this.translateService.instant('JUNE'), 3),
        7: new LimitToPipe().transform(this.translateService.instant('JULY'), 3),
        8: new LimitToPipe().transform(this.translateService.instant('AUGUST'), 3),
        9: new LimitToPipe().transform(this.translateService.instant('SEPTEMBER'), 3),
        10: new LimitToPipe().transform(this.translateService.instant('OCTOBER'), 3),
        11: new LimitToPipe().transform(this.translateService.instant('NOVEMBER'), 3),
        12: new LimitToPipe().transform(this.translateService.instant('DECEMBER'), 3)
      },
      todayBtnTxt: this.translateService.instant('TODAY')
    };
    this.startDatePickerOptions = defaultOptions;
    this.endDatePickerOptions = defaultOptions;
  }

  getDayName(date: Date): string {
    return moment(date).format('dddd').toUpperCase();
  }

  private getJobOrProjectSettings(): void {
    this.activatedRoute.url.subscribe((url: any) => {
      if (url[0].path === 'copy-route') {
        this.editMode = false;
        this.activatedRoute.params.subscribe((params) => {
          if (params['jobId']) {
            this.projectId = params['jobId'];
            this.getJobDetails(params['jobId']);
          }
          if (params['projectId']) {
            this.projectId = params['projectId'];
            this.getProjectDetailsWithoutCopying(this.projectId);
          }
        });
      } else if (url[0].path === 'edit-route') {
        this.editMode = true;
        this.activatedRoute.params.subscribe((params) => {
          if (params['jobId']) {
            let updateRouteVm = this.getDraftRouteFromLocalStorage(params['jobId']);
            if (!updateRouteVm) {
              this.onToProjectOverviewClicked();
              return;
            };
            this.setJobSettingsFromCsv(updateRouteVm);
            this.projectId = updateRouteVm.projectId;
            this.getProjectDetailsWithoutCopying(this.projectId);
            this.showErrors = true;
          }
        });
      } else {
        this.editMode = false;
        this.activatedRoute.params.subscribe((params) => {
          if (params['projectId']) {
            this.projectId = params['projectId'];
            this.getProjectDetails(params['projectId']);
          }
        });
      }
    });
  }

  private getDraftRouteFromLocalStorage(jobId: string): EditRouteViewModel {
    const result = this.newRouteService.getRouteDraftFromLocalStorage(jobId);
    if (!result) { return null; }
    return new EditRouteViewModel(result);
  }

  private setJobSettingsFromCsv(editRouteViewModel: EditRouteViewModel) {
    this.routeName = editRouteViewModel.name;
    this.draftJobId = editRouteViewModel.jobId;
    this.expectedRouteLength = editRouteViewModel.expectedRouteLength;

    if (editRouteViewModel.startDate) {
      let start = new Date(editRouteViewModel.startDate);
      this.fromDatePicker = { date: { year: start.getFullYear(), month: start.getMonth() + 1, day: start.getDate() }, jsdate: start };
      if (this.fromDatePicker && this.fromDatePicker.jsdate) {
        this.startDate = this.fromDatePicker.jsdate;
      }
    }

    if (editRouteViewModel.endDate) {
      let end = editRouteViewModel.endDate ? new Date(editRouteViewModel.endDate) : null;
      this.toDatePicker = { date: { year: end.getFullYear(), month: end.getMonth() + 1, day: end.getDate() }, jsdate: end };
      if (this.toDatePicker && this.toDatePicker.jsdate) {
        this.endDate = this.toDatePicker.jsdate;
      }
    }

    this.calculateSpectrogram = editRouteViewModel.calculateSpectrogram;
    this.calculateBudgetSpectrogram = editRouteViewModel.calculateBudgetSpectrogram;
    this.setHeatmapType();

    this.calculatehistorical = editRouteViewModel.includeHistoricalData;
    this.roadSegment = editRouteViewModel.roadsegment;
    this.interval = editRouteViewModel.interval;
    this.setSelectedDays(editRouteViewModel.days);

    const startTime = editRouteViewModel.startTime ? editRouteViewModel.startTime.split(':') : [];
    const endTime = editRouteViewModel.endTime ? editRouteViewModel.endTime.split(':') : [];

    let startHour = null;
    let startMinutes = null;
    if (startTime.length >= 2) {
      startHour = startTime[0];
      startMinutes = startTime[1];
    }

    let endHour = null;
    let endMinutes = null;
    if (endTime.length >= 2) {
      endHour = endTime[0];
      endMinutes = endTime[1];
    }

    let time = { startHour: startHour, startMinutes: startMinutes, endHour: endHour, endMinutes: endMinutes };
    this.timeInput = time;

    this.setRouteFromCsv(editRouteViewModel);
  }

  private setHeatmapType() {
    if (this.calculateBudgetSpectrogram) {
      this.spectrogram = this.BUDGET_SPEC;
    } else if (this.calculateSpectrogram) {
      this.spectrogram = this.NORMAL_SPEC;
    } else {
      this.spectrogram = this.NO_SPEC;
    }
  }

  private setRouteFromCsv(editRouteViewModel: EditRouteViewModel) {
    if (editRouteViewModel.locations.length === 0) {
      this.expectedRouteLength = null;
      return;
    }
    this.newRouteService.setRoute(editRouteViewModel.locations);
  }

  private getProjectDetailsWithoutCopying(projectId: string): void {
    this.newRouteService.getProjectById(projectId)
      .subscribe((project: Project) => {
        this.project = project;
        this.drawOtherRoutes(project.jobs);
      });
  }

  private getProjectDetails(projectId: string): void {
    this.newRouteService.getProjectById(projectId)
      .subscribe((project: Project) => {
        this.project = project;
        this.drawOtherRoutes(project.jobs);
        if (this.project.jobs.length > 0) {
          let job = this.project.jobs[0];
          this.copyJobSettings(job);
        }
      });
  }

  private getJobDetails(jobId: string): void {
    this.newRouteService.getJobById(jobId)
      .subscribe((job: Job) => {
        this.routeName = job.name + ' -COPY';
        this.copyJobSettings(job);
        this.setRoute(job);
      });
  }

  private copyJobSettings(job: Job): void {
    let start = new Date(job.start);
    let end = new Date(job.end);

    this.fromDatePicker = { date: { year: start.getFullYear(), month: start.getMonth() + 1, day: start.getDate() }, jsdate: start };
    this.toDatePicker = { date: { year: end.getFullYear(), month: end.getMonth() + 1, day: end.getDate() }, jsdate: end };

    if (this.fromDatePicker && this.fromDatePicker.jsdate) {
      this.startDate = this.fromDatePicker.jsdate;
    }
    if (this.toDatePicker && this.toDatePicker.jsdate) {
      this.endDate = this.toDatePicker.jsdate;
    }

    this.calculateBudgetSpectrogram = (job.jobType & JobType.BudgetSpectrogram) === JobType.BudgetSpectrogram;
    this.calculateSpectrogram = (job.jobType & JobType.BudgetSpectrogram) === JobType.BudgetSpectrogram;
    this.branchingAnalyse = (job.jobType & JobType.BranchingAnalyse) === JobType.BranchingAnalyse;
    this.setHeatmapType();

    this.roadSegment = job.minimumSectionLength;
    this.interval = job.schedule.intervalMinutes;
    this.setSelectedDays(job.schedule.days);

    let startTimeArray = job.schedule.startTime.split(':');
    let endTimeArray = job.schedule.endTime.split(':');

    let time = { startHour: startTimeArray[0], startMinutes: startTimeArray[1], endHour: endTimeArray[0], endMinutes: endTimeArray[1] };

    this.timeInputObservable.next(time);
  }

  private setRoute(job: Job): void {
    this.newRouteService.setRoute(job.locations);
  }

  private setSelectedDays(days: number[]): void {
    this.days['sunday'] = false;
    this.days['monday'] = false;
    this.days['tuesday'] = false;
    this.days['wednesday'] = false;
    this.days['thursday'] = false;
    this.days['friday'] = false;
    this.days['saturday'] = false;

    _.each(days, (day: number) => {
      switch (day) {
        case 0:
          this.days['sunday'] = true;
          break;
        case 1:
          this.days['monday'] = true;
          break;
        case 2:
          this.days['tuesday'] = true;
          break;
        case 3:
          this.days['wednesday'] = true;
          break;
        case 4:
          this.days['thursday'] = true;
          break;
        case 5:
          this.days['friday'] = true;
          break;
        case 6:
          this.days['saturday'] = true;
          break;
      }
    });
  }

  getProjectName() {
    if (this.project) {
      return this.project.name;
    }
    return '';
  }

  /* Routing */
  private initMapClick(): void {
    this.subscriptions.push(this.newRouteService.getClickEventObservable()
      .subscribe((location: any) => {
        const markerUrl = '../../../../assets/marker-A.png';
        const markerSize = new Size(27, 45);
        if (!this.newRouteService.startPointIsSet()) {
          this.newRouteService.setStartPoint(location, markerUrl, markerSize);
        } else if (!this.newRouteService.endPointIsSet()) {
          this.newRouteService.setEndPoint(location, markerUrl, markerSize);
        }
      }));
  }

  private initOnRouteChanged(): void {
    this.subscriptions.push(this.newRouteService.getDirectionsChangedObservable()
      .subscribe((route: any) => {
        this.getAddresses(route);
        this.calculateRequestQuota();
        if (this.routeLength !== undefined && this.routeLength !== null) {
          this.expectedRouteLength = null;
        }
        this.routeLength = this.newRouteService.getRouteLength();
      }));
  }

  private drawOtherRoutes(jobs: Array<Job>): void {
    this.newRouteService.drawOtherRoutes(jobs);
  }

  deleteWayPoint(waypoint): void {
    let index = _.indexOf(this.waypoints, waypoint);
    this.waypoints.splice(index, 1);

    let newWaypoints = [];
    _.each(this.waypoints, (wp: WayPointViewModel) => {
      newWaypoints.push(wp.latLng);
    });

    this.newRouteService.drawRouteWithWayPoints(newWaypoints);
  }

  deleteRoute(): void {
    this.startPoint = undefined;
    this.endPoint = undefined;
    this.waypoints = [];
    this.locationsCache = [];

    this.newRouteService.clearRoute();
  }

  reverseRoute(): void {
    const newStartPoint = this.endPoint;
    const newEndPoint = this.startPoint;
    const reversedWaypoints = _.reverse(_.slice(this.waypoints));

    let locations: Position[] = [];

    locations.push(new Position(PositionType.Origin, newStartPoint.latLng.lng, newStartPoint.latLng.lat));
    _.each(reversedWaypoints, (wp: WayPointViewModel) => {
      locations.push(new Position(PositionType.Waypoint, wp.latLng.lng, wp.latLng.lat));
    });
    locations.push(new Position(PositionType.Destination, newEndPoint.latLng.lng, newEndPoint.latLng.lat));

    this.newRouteService.setRoute(locations);
  }

  startPointAdded(): boolean {
    return this.startPoint !== undefined;
  }

  endPointAdded(): boolean {
    return this.endPoint !== undefined;
  }

  waypointsAdded(): boolean {
    return this.waypoints.length > 0;
  }

  reverseGeocodeWaypoint(wayPoint: WayPointViewModel): Promise<any> {
    return new Promise<any>((success, failed) => {
      this.newRouteService.reverseGeocode(wayPoint.latLng)
        .then((result: any) => {
          success(result[0].formatted_address);
        })
        .catch((error: any) => {
          failed('address not found!');
        });
    });
  }

  private async getAddresses(route: any): Promise<void> {
    this.ngZone.run(() => {
      let start = route.routes[0].legs[0].start_location;
      let end = route.routes[0].legs[0].end_location;

      if (!this.startPoint
        || this.startPoint.latLng.lat !== start.lat()
        || this.startPoint.latLng.lng !== start.lng()) {

        let foundPoint = this.findCachedPoint(start);

        if (foundPoint) {
          this.startPoint = foundPoint;
        } else {
          this.newRouteService.reverseGeocode(start).then((result: any) => {
            this.startPoint = new WayPointViewModel();
            this.startPoint.address = result[0].formatted_address;
            this.startPoint.latLng = { lat: start.lat(), lng: start.lng() };
            this.locationsCache.push(this.startPoint);
          })
            .catch((error: any) => {
              this.startPoint.address = 'address not found!';
            });
        }
      }
      if (!this.endPoint
        || this.endPoint.latLng.lat !== end.lat()
        || this.endPoint.latLng.lng !== end.lng()) {

        let foundPoint = this.findCachedPoint(end);

        if (foundPoint) {
          this.endPoint = foundPoint;
        } else {
          this.newRouteService.reverseGeocode(end).then((result: any) => {
            this.endPoint = new WayPointViewModel();
            this.endPoint.address = result[0].formatted_address;
            this.endPoint.latLng = { lat: end.lat(), lng: end.lng() };
            this.locationsCache.push(this.endPoint);
          })
            .catch((error: any) => {
              this.endPoint.address = 'address not found!';
            });
        }
      }

      let waypoints = [];
      for (let wp of route.routes[0].legs[0].via_waypoints) {
        let foundPoint = this.findCachedPoint(wp);

        if (foundPoint) {
          waypoints.push(foundPoint);
        } else {
          let newWp = new WayPointViewModel();
          newWp.latLng = { lat: wp.lat(), lng: wp.lng() };
          waypoints.push(newWp);
        }
      }
      this.waypoints = waypoints;

      _.each(this.waypoints, (wp: WayPointViewModel) => {
        if (wp.address === undefined) {
          this.reverseGeocodeWaypoint(wp).then((result: string) => {
            wp.address = result;
            this.locationsCache.push(wp);
          }).catch((error: string) => {
            wp.address = error;
          });
        }
      });
    });
  }

  private findCachedPoint(point: any): WayPointViewModel {
    return _.find(this.locationsCache, (wp: WayPointViewModel) => {
      return wp.latLng.lat === point.lat() && wp.latLng.lng === point.lng();
    });
  }

  /* Route Settings */
  timeChanged(newTime: any): void {
    this.time = newTime;
    this.calculateRequestQuota();

    let sHour: string = this.parseTimePart(newTime.startHour);
    let sMinutes: string = this.parseTimePart(newTime.startMinutes);
    let eHour: string = this.parseTimePart(newTime.endHour);
    let eMinutes: string = this.parseTimePart(newTime.endMinutes);

    this.startTimeString = sHour + ':' + sMinutes + ':00';
    this.endTimeString = eHour + ':' + eMinutes + ':00';
  }

  private parseTimePart(timePartString: string): string {
    if (timePartString === undefined || timePartString === null) { return ''; }

    const timePart = tryParseInt(timePartString);
    if (timePart === 0) { return '00'; }

    return timePart < 10 ? '0' + timePart : timePartString;
  }

  onStartDateChanged(event: IMyDateModel): void {
    this.startDate = event.jsdate;
    this.calculateRequestQuota();
    this.disableUntil(this.startDate);
  }

  private disableUntil(date: Date) {
    let d: Date = new Date(date);
    d.setDate(d.getDate() - 1);
    let copy = this.getCopyOfOptions();
    copy.disableUntil = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate()
    };
    this.endDatePickerOptions = copy;
  }

  private getCopyOfOptions(): IMyOptions {
    return JSON.parse(JSON.stringify(this.endDatePickerOptions));
  }

  onEndDateChanged(event: IMyDateModel): void {
    this.endDate = event.jsdate;
    this.calculateRequestQuota();
  }

  /* Calculation */
  createRoute(): void {
    if (this.isFormValid()) {
      this.showErrors = false;
      let routeData = new NewRouteViewModel();

      routeData.name = this.routeName;
      routeData.startDate = this.startDate;
      routeData.endDate = this.endDate;
      routeData.startTime = this.startTimeString;
      routeData.endTime = this.endTimeString;
      routeData.roadsegment = this.roadSegment !== null ? this.roadSegment : 150;
      routeData.interval = this.interval;
      routeData.route = this.newRouteService.getCurrentRoute();
      routeData.days = this.selectedDays;
      routeData.calculateSpectrogram = this.calculateSpectrogram;
      routeData.calculateBudgetSpectrogram = this.calculateBudgetSpectrogram;
      routeData.includeHistoricalData = this.calculatehistorical;
      routeData.branchingAnalyse = this.branchingAnalyse;
      if (this.project) {
        routeData.projectId = this.projectId;
      }

      this.calculateRequestQuota();
      this.newRouteService.saveNewRoute(routeData)
        .subscribe((jobId: string) => {
          if (jobId) {
            this.checkCreationProgress(jobId, 0);
          } else {
            this.createdFailed = true;
          }
        }, (error: any) => {
          this.createdFailed = true;
        });
    } else {
      this.showErrors = true;
    }
  }

  private calculateRequestQuota(): void {
    let routeLength = this.newRouteService.getRouteLength();
    let dates: Date[] = [];
    if (this.startDate && this.endDate) {
      dates = this.newRouteService.countDays(this.startDate, this.endDate, this.getSelectedDays());
    }

    const startTime = this.getTime(this.time.startHour, this.time.startMinutes);
    const endTime = this.getTime(this.time.endHour, this.time.endMinutes);
    let snapshots = this.newRouteService.calculateRequestsPerDay(startTime, endTime, this.interval);
    let historicDays = this.newRouteService.calculateHistoricalDays(this.startDate, this.endDate, this.selectedDays);

    if (historicDays > 0 && routeLength > 0 && dates.length > 0) {
      let calculateRequest = new CalculateQuotaRequest(routeLength, dates,
        this.roadSegment, snapshots, historicDays, this.calculateSpectrogram, this.calculateBudgetSpectrogram, this.calculatehistorical, this.branchingAnalyse);

      this.newRouteService.calculateQuota(calculateRequest)
        .then((result: CalculateQuotaResponse) => {
          this.requestsPerDay = result.requestsPerDay;
          this.insufficientQuota = result.quotaInsufficient;
          this.quotaShortage = result.shortage;
        });
      this.selectedDaysValid = true;
    } else {
      this.selectedDaysValid = false;
    }
  }

  private getTime(hour: number, minutes: number): Date {
    let result = new Date();
    result.setHours(hour);
    result.setMinutes(minutes);
    return result;
  }

  recalculatePerDay(): void {
    this.calculateSpectrogram = this.spectrogram === 'calculateSpectrogram';
    this.calculateBudgetSpectrogram = this.spectrogram === 'calculateBudgetSpectrogram';

    this.calculateRequestQuota();
  }

  private getSelectedDays(): number[] {
    this.selectedDays = [];
    _.keys(this.days).forEach((key) => {
      if (this.days[key]) {
        switch (key) {
          case 'sunday':
            this.selectedDays.push(0);
            break;
          case 'monday':
            this.selectedDays.push(1);
            break;
          case 'tuesday':
            this.selectedDays.push(2);
            break;
          case 'wednesday':
            this.selectedDays.push(3);
            break;
          case 'thursday':
            this.selectedDays.push(4);
            break;
          case 'friday':
            this.selectedDays.push(5);
            break;
          case 'saturday':
            this.selectedDays.push(6);
            break;
        }
      }
    });
    return this.selectedDays;
  }

  private checkCreationProgress(jobId: string, counter: number): void {
    this.createInProgress = true;
    setTimeout(() => {
      if (counter < 20) {
        counter++;
        this.newRouteService.getJobStatus(jobId).subscribe((status: JobStatus) => {
          if (status === JobStatus.Submitted) {
            this.checkCreationProgress(jobId, counter);
          } else if (status === JobStatus.QuotaInsufficient) {
            this.createInProgress = false;
            this.createdFailed = true;
          } else {
            this.newRouteService.deleteRouteDraftFromLocalStorage(this.draftJobId);
            this.createInProgress = false;
            this.createdShown = true;
          }
        }, (error: any) => {
          this.checkCreationProgress(jobId, counter);
        });
      } else {
        // TODO show message, creation taking too long.
        this.createInProgress = false;
        this.createdFailed = true;
      }
    }, 3000);
  }

  /* View */
  showCreateSuccess(): boolean {
    return this.createdShown;
  }

  showCreateFailed(): boolean {
    return this.createdFailed;
  }
  showCreateInProgress(): boolean {
    return this.createInProgress;
  }

  showNewRoute(): boolean {
    return !this.createdShown && !this.createdFailed && !this.createInProgress;
  }

  showNewRouteDialogn() {
    this.newRouteDialogVisible = true;
    this.hideTutorial = false;
    this.newRouteService.focusSearch();
  }

  newRouteDialogIsVisible(): boolean {
    return this.newRouteDialogVisible && !this.hideTutorial;
  }

  newRouteDialogOk(hideNextTime: boolean): void {
    this.newRouteDialogVisible = false;
    this.hideTutorial = true;
    this.updateUserSettings();
  }

  getUserSettings(): void {
    this.newRouteService.getUserSettings().then((userData: UserData) => {
      if (!_.isUndefined(userData.hideTutorial)) {
        this.hideTutorial = userData.hideTutorial;
      } else {
        this.hideTutorial = false;
      }
    });
  }

  updateUserSettings(): void {
    let userdata = new UserData();
    userdata.hideTutorial = this.hideTutorial;
    this.newRouteService.updateUserSettings(userdata);
  }

  onToRouteOverviewClicked(): void {
    this.router.navigate(['auth']);
  }

  onToProjectOverviewClicked(): void {
    this.router.navigate(['auth/projects']);
  }

  onNewRouteClicked(): void {
    this.createdShown = false;
    this.createdFailed = false;
    this.routeName = '';
    this.ngOnDestroy();
    this.ngOnInit();
  }

  /* Validation */
  isFormValid(): boolean {
    return this.datesValid() && this.timeValid() && this.routeValid() && this.nameValid() &&
      this.hasSelectedDays() && this.selectedDaysValid && this.sectionLengthValid() && this.requestIntervalValid();
  }

  datesValid(): boolean {
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    return ((this.startDate && this.endDate) && (this.startDate >= today) && (this.endDate >= this.startDate));
  }

  timeValid(): boolean {
    let start = this.startTimeString.split(':');
    let end = this.endTimeString.split(':');
    let startHour = tryParseInt(start[0], 10);
    let startMinutes = tryParseInt(start[1], 10);
    let endHour = tryParseInt(end[0], 10);
    let endMinutes = tryParseInt(end[1], 10);

    if (startHour === null || startMinutes === null || endHour === null || endMinutes === null) {
      return false;
    }

    return (startHour >= 0 && startMinutes <= 59
      && endHour <= 23 && endMinutes <= 59
      && (endHour > startHour || (endHour === startHour && endMinutes > startMinutes)));
  }

  nameValid(): boolean {
    return this.routeName !== null && this.routeName !== undefined && this.routeName.length > 0;
  }

  hasSelectedDays(): boolean {
    return this.selectedDays && this.selectedDays.length > 0;
  }

  hasInvalidSelectedDays(): boolean {
    return this.routeValid() && this.datesValid() && this.hasSelectedDays() && !this.selectedDaysValid;
  }

  routeValid(): boolean {
    const currentRoute = this.newRouteService.getCurrentRoute();
    const hasRoute = currentRoute && currentRoute.routes && currentRoute.routes.length > 0;
    return hasRoute;
  }

  sectionLengthValid(): boolean {
    return !this.calculateSpectrogram || this.roadSegment !== null;
  }

  requestIntervalValid(): boolean {
    return this.interval !== null;
  }

  hasExpectedRouteLength(): boolean {
    return this.editMode && this.expectedRouteLength !== null && this.expectedRouteLength !== undefined;
  }

  errorsShown(): boolean {
    return this.showErrors;
  }

  quotaInsufficient(): boolean {
    return this.insufficientQuota;
  }

  ngOnDestroy() {
    this.newRouteService.clearCachedPolyLines();
    this.newRouteService.clearOtherRoutes();
    this.newRouteService.disableSearch();
    _.each(this.subscriptions, (sub: Subscription) => {
      sub.unsubscribe();
    });
    this.locationsCache = [];
  }

  isEditMode(): boolean {
    return this.editMode;
  }
  // branchingAnalyse(){
  // return this.branchingAnalyse;
  // }
}



// WEBPACK FOOTER //
// ./src/app/modules/new-route/new-route.component.ts